import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectService } from '../modules/project.service';
import { WorkShiftRotationService } from '../modules/work-shift-rotation.service';
import { CompaniesService } from '../modules/companies.service';
import { EmployerService } from '../modules/employer.service';
import { WorkShiftConfiguration } from '../../interfaces/interfaces';
import {
  BulkRotationCard,
  BulkSaveResultApi,
  buildBulkRotationCard,
  buildShiftPatternsFromWorkShifts,
  MappedBulkSchedule,
  mapBulkScheduleResponse,
  ShiftPattern
} from '../../utils/work-schedule-bulk.util';

export interface BulkContractorCompany {
  employerId: string;
  name: string;
}

/**
 * Único punto con efectos secundarios (HTTP) para "Programación masiva de horarios". La lógica
 * de mapeo pura vive en work-schedule-bulk.util.ts; este servicio solo llama a la API y delega
 * el mapeo a esas funciones.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkScheduleBulkDataService {

  constructor(
    private projectService: ProjectService,
    private companiesService: CompaniesService,
    private employerService: EmployerService,
    private rotationService: WorkShiftRotationService
  ) {}

  getScheduleBulk(
    projectId: string,
    from: string,
    to: string,
    employerInfoId: string | null,
    page: number,
    pageSize: number
  ): Observable<MappedBulkSchedule> {
    let url = `api/Project/${projectId}/Schedule/Bulk?from=${from}&to=${to}&page=${page}&pageSize=${pageSize}`;
    if (employerInfoId) { url += `&employerInfoId=${employerInfoId}`; }
    return this.projectService.getScheduleBulk(url).pipe(map(mapBulkScheduleResponse));
  }

  saveScheduleBulk(
    projectId: string,
    from: string,
    to: string,
    people: Array<{ personalEmployerId: number; days: Array<{ date: string; workShiftId: string | null }> }>
  ): Observable<BulkSaveResultApi> {
    return this.projectService.postScheduleBulk(projectId, { from, to, people });
  }

  /** "Limpiar" en la masiva no tiene un endpoint de borrado propio (a diferencia de la
   * individual, que usa un DELETE) — es el mismo POST de guardado con `personalEmployerIds` +
   * `days: []` como plantilla común, que el backend interpreta como "dejar sin programar". */
  clearScheduleBulk(
    projectId: string,
    from: string,
    to: string,
    personalEmployerIds: number[]
  ): Observable<BulkSaveResultApi> {
    return this.projectService.postScheduleBulk(projectId, { from, to, personalEmployerIds, days: [] });
  }

  /** Catálogo completo de turnos y patrones del proyecto, para pintar los chips arrastrables
   * del sidebar — a diferencia del catálogo embebido en Schedule/Bulk (que sólo trae los turnos
   * que ya aparecen asignados en la página de personas cargada), este trae siempre todos. */
  getProjectWorkShifts(projectId: string): Observable<ShiftPattern[]> {
    return this.projectService.getWorkShift(projectId).pipe(
      map((list: WorkShiftConfiguration[]) => buildShiftPatternsFromWorkShifts(list))
    );
  }

  /** Patrones catorcenales activos del proyecto, resueltos contra los turnos ya cargados. Solo
   * informativos en la masiva; un fallo del catálogo no debe tumbar la pantalla. */
  getProjectRotations(projectId: string, patterns: ShiftPattern[]): Observable<BulkRotationCard[]> {
    if (!projectId) { return of([]); }
    return this.rotationService.getByProject(projectId).pipe(
      map(list => list.filter(r => r.isActive).map(r => buildBulkRotationCard(r, patterns))),
      catchError(() => of([]))
    );
  }

  getMaxWeeklyHours(adminInfoId: string): Observable<number> {
    return this.companiesService.getAdminConfiguration(adminInfoId).pipe(
      map((config: any) => config?.MaxWorkedTimeByWeek ?? 0)
    );
  }

  /** Proyectos disponibles para el combo "Nombre del proyecto" del encabezado. */
  getProjectsForCompany(companyInfoId: string): Observable<BulkProjectOption[]> {
    return this.projectService.getProjectSelected(companyInfoId).pipe(
      map((projects: any[]) => (projects || [])
        .map(p => ({ id: p.Id, name: p.Name, isActive: p.IsActive !== false }))
        .sort((a, b) => {
          if (a.isActive !== b.isActive) { return a.isActive ? -1 : 1; }
          return (a.name || '').localeCompare(b.name || '');
        }))
    );
  }

  /** Empresas contratistas para el combo "Empresa" — mismas para todos los proyectos de la
   * empresa administradora (companyInfoId), así que no hace falta recargarlas al cambiar de
   * proyecto en el combo "Proyectos". */
  getCompanyEmployers(companyInfoId: string): Observable<BulkContractorCompany[]> {
    return this.employerService.getEmployersForPayrollTimes(companyInfoId).pipe(
      map((employers) => (employers || []).map(e => ({ employerId: e.Id, name: e.Name })))
    );
  }
}

export interface BulkProjectOption {
  id: string;
  name: string;
  isActive: boolean;
}
